// MET Norway Locationforecast 2.0 — free, no API key, commercial use allowed (NLOD + CC BY 4.0)
// Docs: https://api.met.no/weatherapi/locationforecast/2.0/swagger
// Terms require a User-Agent identifying the app and contact email.
// Forecast window: ~10 days ahead. No historical data.

export type WeatherCondition =
    | "clear"
    | "mainly_clear"
    | "partly_cloudy"
    | "overcast"
    | "fog"
    | "drizzle"
    | "freezing_drizzle"
    | "rain"
    | "freezing_rain"
    | "snow"
    | "snow_grains"
    | "rain_showers"
    | "snow_showers"
    | "thunderstorm";

export type WeatherData = {
    temperature: number | null;
    condition: WeatherCondition;
    conditionKey: string;
    icon: string;
    humidity: number | null;
    uvIndex: number | null;
    windspeed: number | null;
    windDirectionDeg: number | null;
    windDirectionLabel: string | null;
    precipitation: number | null;
};

type SymbolEntry = { condition: WeatherCondition; key: string; icon: string };

// MET Norway symbol codes — strip _day / _night / _polartwilight suffix before lookup
const SYMBOL_CODES: Record<string, SymbolEntry> = {
    clearsky:                    { condition: "clear",         key: "weather.condition.clear",             icon: "☀️" },
    fair:                        { condition: "mainly_clear",  key: "weather.condition.mainlyClear",       icon: "🌤️" },
    partlycloudy:                { condition: "partly_cloudy", key: "weather.condition.partlyCloudy",      icon: "⛅" },
    cloudy:                      { condition: "overcast",      key: "weather.condition.overcast",          icon: "☁️" },
    fog:                         { condition: "fog",           key: "weather.condition.fog",               icon: "🌫️" },
    lightrain:                   { condition: "rain",          key: "weather.condition.lightRain",         icon: "🌧️" },
    rain:                        { condition: "rain",          key: "weather.condition.moderateRain",      icon: "🌧️" },
    heavyrain:                   { condition: "rain",          key: "weather.condition.heavyRain",         icon: "🌧️" },
    lightrainshowers:            { condition: "rain_showers",  key: "weather.condition.lightRainShowers",  icon: "🌦️" },
    rainshowers:                 { condition: "rain_showers",  key: "weather.condition.rainShowers",       icon: "🌦️" },
    heavyrainshowers:            { condition: "rain_showers",  key: "weather.condition.heavyRainShowers",  icon: "🌧️" },
    lightsleet:                  { condition: "freezing_rain", key: "weather.condition.freezingRain",      icon: "🌧️" },
    sleet:                       { condition: "freezing_rain", key: "weather.condition.freezingRain",      icon: "🌧️" },
    heavysleet:                  { condition: "freezing_rain", key: "weather.condition.heavyFreezingRain", icon: "🌧️" },
    lightsleetshowers:           { condition: "freezing_rain", key: "weather.condition.freezingRain",      icon: "🌧️" },
    sleetshowers:                { condition: "freezing_rain", key: "weather.condition.freezingRain",      icon: "🌧️" },
    heavysleetshowers:           { condition: "freezing_rain", key: "weather.condition.heavyFreezingRain", icon: "🌧️" },
    lightsnow:                   { condition: "snow",          key: "weather.condition.lightSnow",         icon: "🌨️" },
    snow:                        { condition: "snow",          key: "weather.condition.moderateSnow",      icon: "🌨️" },
    heavysnow:                   { condition: "snow",          key: "weather.condition.heavySnow",         icon: "🌨️" },
    lightsnowshowers:            { condition: "snow_showers",  key: "weather.condition.lightSnowShowers",  icon: "🌨️" },
    snowshowers:                 { condition: "snow_showers",  key: "weather.condition.rainShowers",       icon: "🌨️" },
    heavysnowshowers:            { condition: "snow_showers",  key: "weather.condition.heavySnowShowers",  icon: "🌨️" },
    lightrainandthunder:         { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    rainandthunder:              { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    heavyrainandthunder:         { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    lightsleetandthunder:        { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    sleetandthunder:             { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    heavysleetandthunder:        { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    lightsnowandthunder:         { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    snowandthunder:              { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    heavysnowandthunder:         { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    lightrainshowersandthunder:  { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    rainshowersandthunder:       { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    heavyrainshowersandthunder:  { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    lightsleetshowersandthunder: { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    sleetshowersandthunder:      { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    heavysleetshowersandthunder: { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    lightsnowshowersandthunder:  { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    snowshowersandthunder:       { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
    heavysnowshowersandthunder:  { condition: "thunderstorm",  key: "weather.condition.thunderstorm",      icon: "⛈️" },
};

const FALLBACK: SymbolEntry = { condition: "clear", key: "weather.condition.unknown", icon: "🌡️" };

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
function degreesToCompass(deg: number): string {
    return COMPASS[Math.round(((deg % 360) + 360) % 360 / 45) % 8];
}

export type UvRisk = "low" | "moderate" | "high" | "very_high" | "extreme";

export function uvRisk(index: number): UvRisk {
    if (index < 3)  return "low";
    if (index < 6)  return "moderate";
    if (index < 8)  return "high";
    if (index < 11) return "very_high";
    return "extreme";
}

/**
 * Fetches hourly weather from MET Norway for a given location and datetime.
 * Only works for dates within the next ~10 days (no historical support).
 *
 * @param lat   Latitude
 * @param lng   Longitude
 * @param date  Target datetime — used as-is to find the nearest timeseries entry.
 */
export async function fetchWeatherForLocation(
    lat: number,
    lng: number,
    date: Date,
): Promise<WeatherData | null> {
    const url = new URL("https://api.met.no/weatherapi/locationforecast/2.0/complete");
    url.searchParams.set("lat", lat.toFixed(4));
    url.searchParams.set("lon", lng.toFixed(4));

    try {
        const response = await fetch(url.toString(), {
            headers: {
                "User-Agent": "TimeOnTrails trail-planner luiskcs89@gmail.com",
            },
        });
        if (!response.ok) throw new Error(`MET Norway ${response.status}`);

        const data = await response.json();
        const timeseries: any[] = data.properties.timeseries;

        // Find timeseries entry closest to the target datetime
        let closest: any = null;
        let minDiff = Infinity;
        for (const entry of timeseries) {
            const diff = Math.abs(new Date(entry.time).getTime() - date.getTime());
            if (diff < minDiff) { minDiff = diff; closest = entry; }
        }
        if (!closest) return null;

        const instant = closest.data.instant.details;
        const next1h = closest.data.next_1_hours;
        const next6h = closest.data.next_6_hours;

        const rawSymbol: string =
            next1h?.summary?.symbol_code ?? next6h?.summary?.symbol_code ?? "clearsky_day";
        // Strip day/night/polartwilight suffix
        const symbolKey = rawSymbol.replace(/_(day|night|polartwilight)$/, "");
        const entry = SYMBOL_CODES[symbolKey] ?? FALLBACK;

        const windDeg = instant.wind_from_direction ?? null;
        const precip = next1h?.details?.precipitation_amount ?? next6h?.details?.precipitation_amount ?? null;

        return {
            temperature:        instant.air_temperature != null ? Math.round(instant.air_temperature) : null,
            condition:          entry.condition,
            conditionKey:       entry.key,
            icon:               entry.icon,
            humidity:           instant.relative_humidity != null ? Math.round(instant.relative_humidity) : null,
            uvIndex:            instant.ultraviolet_index_clear_sky != null
                                    ? Math.round(instant.ultraviolet_index_clear_sky * 10) / 10
                                    : null,
            windspeed:          instant.wind_speed != null ? Math.round(instant.wind_speed * 3.6) : null,
            windDirectionDeg:   windDeg != null ? Math.round(windDeg) : null,
            windDirectionLabel: windDeg != null ? degreesToCompass(windDeg) : null,
            precipitation:      precip,
        };
    } catch (error) {
        console.error("[WeatherUtils] fetchWeatherForLocation:", error);
        return null;
    }
}
